import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import { useMutation } from "@apollo/client/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { VERIFY_PASS_PAYMENT, VERIFY_EVENT_PAYMENT, VERIFY_CART_PAYMENT } from "../graphql/mutation";
import { useCart } from "../context/CartContext";

type UIStatus = "processing" | "success" | "pending" | "error";

const ICON_MAP: Record<UIStatus, React.ReactNode> = {
  processing: <CircularProgress sx={{ color: "#6CBC44", mb: 2 }} />,
  success:    <CheckCircleOutlineIcon sx={{ color: "#6CBC44", fontSize: 48, mb: 2 }} />,
  pending:    <HourglassEmptyOutlinedIcon sx={{ color: "#F5A623", fontSize: 48, mb: 2 }} />,
  error:      <ErrorOutlineIcon sx={{ color: "#EF4444", fontSize: 48, mb: 2 }} />,
};

const TITLE_MAP: Record<UIStatus, string> = {
  processing: "Processing payment",
  success:    "Payment successful",
  pending:    "Payment pending",
  error:      "Payment failed",
};

const PaymentCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState("Verifying your payment…");
  const [status, setStatus] = useState<UIStatus>("processing");
  const [verifyPassPayment] = useMutation(VERIFY_PASS_PAYMENT);
  const [verifyEventPayment] = useMutation(VERIFY_EVENT_PAYMENT);
  const [verifyCartPayment] = useMutation(VERIFY_CART_PAYMENT);
  const { clearCart } = useCart();

  useEffect(() => {
    const source   = searchParams.get("source");
    const redirect = searchParams.get("redirect") || "/passes";
    const orderId  = searchParams.get("order_id");
    const buildRedirectUrl = (base: string, status: string, message: string) => {
      const sep = base.includes("?") ? "&" : "?";
      return `${base}${sep}paymentStatus=${status}&paymentMessage=${encodeURIComponent(message)}`;
    };

    const finalize = (
      finalStatus: "success" | "pending" | "error",
      text: string,
      redirectTarget?: string
    ) => {
      setStatus(finalStatus);
      setMessage(text);
      const base = redirectTarget ?? redirect;
      const statusLabel = finalStatus === "success" ? "success" : "failed";
      const target = buildRedirectUrl(base, statusLabel, text);
      setTimeout(() => navigate(target, { replace: true }), 2500);
    };

    const run = async () => {
      if (!source || !orderId) {
        finalize("error", "Missing payment details. Please contact support.");
        return;
      }

      try {
        if (source === "pass") {
          const { data } = await verifyPassPayment({ variables: { orderId } });
          const res = data?.verifyPassPayment;
          if (res?.success) {
            finalize("success", res.message || "Payment completed successfully.");
          } else {
            finalize("error", res?.message || "Payment was not successful. Please try again.");
          }
          return;
        }

        if (source === "event") {
          const { data } = await verifyEventPayment({ variables: { orderId } });
          const res = data?.verifyEventPayment;
          if (res?.success) {
            finalize("success", res.message || "Event booking confirmed!");
          } else {
            finalize("error", res?.message || "Payment was not successful. Please try again.");
          }
          return;
        }

        if (source === "cart") {
          const { data } = await verifyCartPayment({ variables: { orderId } });
          const res = data?.verifyCartPayment;
          if (res?.success) {
            clearCart();
            finalize("success", res.message || "Cart purchase successful!");
          } else {
            finalize("error", res?.message || "Payment was not successful. Please try again.");
          }
          return;
        }
        finalize("error", "Unsupported payment source.");
      } catch {
        finalize(
          "error",
          "Payment verification failed. Please check your passes or bookings shortly."
        );
      }
    };

    run();
  }, [navigate, searchParams, verifyPassPayment, verifyEventPayment, verifyCartPayment, clearCart]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        background: "linear-gradient(180deg, #F7FAF3 0%, #FFFFFF 100%)",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          p: 4,
          borderRadius: "24px",
          backgroundColor: "#fff",
          textAlign: "center",
          boxShadow: "0px 20px 45px rgba(17, 24, 39, 0.08)",
          border: "1px solid #E8EEE3",
        }}
      >
        {ICON_MAP[status]}
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#111827", mb: 1 }}>
          {TITLE_MAP[status]}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#6B7280", lineHeight: 1.7 }}>
          {message}
        </Typography>
      </Box>
    </Box>
  );
};

export default PaymentCallback;
